import React, { useState, useEffect } from 'react';
import { adminApi } from '@/lib/api';
import { Users, Shield, ChevronDown, Loader2 } from 'lucide-react';

const DISCOUNT_LEVELS = [
  { value: 'standard', label: 'Standard', limit: '10%', description: 'Default for new users' },
  { value: 'manager', label: 'Manager', limit: '15%', description: 'For team managers' },
  { value: 'vp', label: 'VP', limit: '20%', description: 'For VPs and directors' },
  { value: 'admin', label: 'Admin', limit: 'Unlimited', description: 'Full discount authority' },
];

export function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await adminApi.listUsers();
      setUsers(data);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDiscountLevelChange = async (userId, newLevel) => {
    try {
      setUpdating(userId);
      await adminApi.updateDiscountLevel(userId, newLevel);
      setUsers(users.map(u => 
        u.id === userId ? { ...u, discount_level: newLevel } : u
      ));
      setMessage({ type: 'success', text: 'Discount level updated successfully' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to update discount level' });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setUpdating(null);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      setUpdating(userId);
      await adminApi.updateUserRole(userId, newRole);
      setUsers(users.map(u => 
        u.id === userId ? { ...u, role: newRole } : u
      ));
      setMessage({ type: 'success', text: 'User role updated successfully' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to update user role' });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setUpdating(null);
    }
  };

  const handleStatusToggle = async (userId, currentStatus) => {
    try {
      setUpdating(userId);
      await adminApi.toggleUserStatus(userId, !currentStatus);
      setUsers(users.map(u => 
        u.id === userId ? { ...u, is_active: !currentStatus } : u
      ));
      setMessage({ type: 'success', text: `User ${!currentStatus ? 'activated' : 'deactivated'}` });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to update user status' });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setUpdating(null);
    }
  };

  const getLevelBadgeColor = (level) => {
    switch (level) {
      case 'admin': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'vp': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'manager': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8">
        <div className="flex items-center justify-center gap-3">
          <Loader2 className="animate-spin text-[#3c68ff]" size={20} />
          <span className="text-slate-500">Loading users...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm" data-testid="user-management-card">
      <div className="p-5 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="text-[#3c68ff]" size={20} />
          <h2 className="text-base font-semibold font-['Outfit'] text-slate-900">User Management</h2>
          <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{users.length} users</span>
        </div>
        {message && (
          <span className={`text-xs font-medium px-3 py-1 rounded-full ${
            message.type === 'success' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
          }`}>
            {message.text}
          </span>
        )}
      </div>

      {/* Discount Level Legend */}
      <div className="p-4 bg-slate-50/50 border-b border-slate-100">
        <div className="flex items-center gap-2 mb-2">
          <Shield size={14} className="text-slate-400" />
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Discount Limits by Level</span>
        </div>
        <div className="flex flex-wrap gap-3">
          {DISCOUNT_LEVELS.map(level => (
            <div key={level.value} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${getLevelBadgeColor(level.value)}`}>
              <span className="text-xs font-semibold capitalize">{level.label}</span>
              <span className="text-xs opacity-75">({level.limit})</span>
            </div>
          ))}
        </div>
      </div>

      {/* Users Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50/80 border-b border-slate-100">
              <th className="px-4 py-3 text-left font-semibold text-slate-500 uppercase tracking-wider text-xs">User</th>
              <th className="px-4 py-3 text-left font-semibold text-slate-500 uppercase tracking-wider text-xs">Role</th>
              <th className="px-4 py-3 text-left font-semibold text-slate-500 uppercase tracking-wider text-xs">Discount Level</th>
              <th className="px-4 py-3 text-center font-semibold text-slate-500 uppercase tracking-wider text-xs">Status</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                <td className="px-4 py-3">
                  <div>
                    <div className="font-medium text-slate-900">{user.full_name}</div>
                    <div className="text-xs text-slate-500">{user.email}</div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <select
                    data-testid={`role-select-${user.id}`}
                    value={user.role}
                    onChange={(e) => handleRoleChange(user.id, e.target.value)}
                    disabled={updating === user.id}
                    className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#3c68ff] disabled:opacity-50"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </td>
                <td className="px-4 py-3">
                  <div className="relative">
                    <select
                      data-testid={`discount-level-${user.id}`}
                      value={user.discount_level || 'standard'}
                      onChange={(e) => handleDiscountLevelChange(user.id, e.target.value)}
                      disabled={updating === user.id}
                      className={`h-8 rounded-lg border px-3 pr-8 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-[#3c68ff] disabled:opacity-50 appearance-none cursor-pointer ${getLevelBadgeColor(user.discount_level || 'standard')}`}
                    >
                      {DISCOUNT_LEVELS.map(level => (
                        <option key={level.value} value={level.value}>
                          {level.label} ({level.limit})
                        </option>
                      ))}
                    </select>
                    <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none opacity-50" />
                  </div>
                </td>
                <td className="px-4 py-3 text-center">
                  <button
                    data-testid={`status-toggle-${user.id}`}
                    onClick={() => handleStatusToggle(user.id, user.is_active)}
                    disabled={updating === user.id}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                      user.is_active 
                        ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' 
                        : 'bg-red-100 text-red-700 hover:bg-red-200'
                    } disabled:opacity-50`}
                  >
                    {updating === user.id ? (
                      <Loader2 className="animate-spin inline" size={12} />
                    ) : (
                      user.is_active ? 'Active' : 'Inactive'
                    )}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
